import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useData } from '../../contexts/DataContext';
import { useLanguage } from '../../contexts/LanguageContext';
import MobileLayout from '../layout/MobileLayout';
import MobileBottomNav from '../layout/MobileBottomNav';
import { LoadingSpinner } from '../common/LoadingSpinner';
import Button from '../common/Button';
import ShareSheet from '../common/ShareSheet';
import EditContactModal from './EditContactModal';
import { DetailPaneShell } from '../common/DetailPaneShell';
import { ContactTasksSection } from './ContactTasksSection';
import { CopyContactToWorkspace } from './CopyContactToWorkspace';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { toast } from 'sonner';
import { confirmAction } from '../../utils/confirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactDetailsPage = ({ contactId: paneContactId, embedded = false, onClose, onBack, onSelectTask, onChanged }) => {
  const params = useParams();
  const contactId = paneContactId || params.contactId;
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { deleteContact } = useData();
  const { workspaces = [] } = useWorkspace() || {};
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  // Share sheet (popover on desktop, bottom sheet on mobile).
  const [shareOpen, setShareOpen] = useState(false);
  const shareBtnRef = useRef(null);

  useEffect(() => {
    fetchContact();
  }, [contactId]);

  const fetchContact = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API}/contacts/${contactId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setContact(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching contact:', error);
      setError('Failed to load contact details');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Smart navigation: always go back to contacts list (parent page)
    // Use replace to avoid adding to history stack
    if (embedded) (onBack || onClose)?.();
    else navigate('/contacts', { replace: true });
  };

  const renderLayout = (children, rightAction) => (embedded ? (
    <DetailPaneShell
      title={t('contact') === 'contact' ? 'Contact' : t('contact')}
      onClose={onClose}
      onBack={onBack}
      backLabel={t('backToTask') === 'backToTask' ? 'Back to task' : t('backToTask')}
      rightAction={rightAction}
    >
      {children}
    </DetailPaneShell>
  ) : (
    <MobileLayout title={t('contactDetails')} showBack onBack={handleBack} rightAction={rightAction} onRefresh={() => fetchContact(true)}>{children}</MobileLayout>
  ));

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (!(await confirmAction({ title: 'Delete this contact?', message: 'This cannot be undone.', confirmLabel: t('delete'), destructive: true }))) {
      return;
    }

    try {
      await deleteContact(contactId);
      toast.success('Contact deleted');
      if (embedded) {
        onChanged?.();
        onClose?.();
      } else {
        navigate('/contacts');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const handleEditSuccess = () => {
    fetchContact(); // Refresh contact data after edit
    setShowEditModal(false);
    onChanged?.();
  };

  const handleAddressClick = () => {
    if (contact?.address) {
      const encodedAddress = encodeURIComponent(contact.address);
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapsUrl, '_blank');
    }
  };

  // Sharing — download contact as vCard or PDF business card.
  const downloadFromApi = async (path, fallbackName) => {
    const resp = await fetch(`${API}${path}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!resp.ok) throw new Error(`Server responded ${resp.status}`);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const handleShareVcard = async () => {
    try {
      const safe = (contact?.name || 'contact').replace(/[^\w-]+/g, '_');
      await downloadFromApi(`/contacts/${contactId}/share/vcard`, `${safe}.vcf`);
    } catch (err) {
      console.error('vCard failed:', err);
      alert('Could not generate vCard.');
    }
  };

  const handleSharePdfCard = async () => {
    try {
      const safe = (contact?.name || 'contact').replace(/[^\w-]+/g, '_');
      await downloadFromApi(`/contacts/${contactId}/share/pdf-card`, `${safe}-business-card.pdf`);
    } catch (err) {
      console.error('PDF card failed:', err);
      alert('Could not generate PDF card.');
    }
  };

  if (loading) {
    return renderLayout(
      <>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
        {!embedded && <MobileBottomNav />}
      </>
    );
  }

  if (error || !contact) {
    return renderLayout(
      <>
        <div className="mobile-card p-8 text-center">
          <div className="text-6xl mb-4">😞</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('contactNotFound')}</h3>
          <p className="text-gray-600 mb-4">{error || t('contactNotFoundDesc')}</p>
          <Button onClick={handleBack}>
            {t('goBack')}
          </Button>
        </div>
        {!embedded && <MobileBottomNav />}
      </>
    );
  }

  const initials = contact.name 
    ? contact.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  const myUserId = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').id; } catch { return null; } })();
  const orgRole = workspaces.find((w) => w.id === contact.org_id)?.role;
  const canEdit = !contact.org_id || contact.user_id === myUserId || orgRole === 'owner' || orgRole === 'admin';

  const headerActions = (
        <div className="flex items-center space-x-2 relative">
          {/* Share */}
          <button
            ref={shareBtnRef}
            onClick={() => setShareOpen((v) => !v)}
            className="text-white hover:text-gray-200 lg:text-gray-600 lg:hover:text-blue-600 transition-colors p-1"
            title={t('share') || 'Share'}
            aria-label={t('share') || 'Share'}
            data-testid="contact-share-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </button>
          <ShareSheet
            open={shareOpen}
            onClose={() => setShareOpen(false)}
            anchorRef={shareBtnRef}
            title={t('shareThisContact') || 'Share this contact'}
            options={[
              {
                id: 'vcard',
                icon: '👤',
                label: t('sendAsVCard') || 'Send as vCard (.vcf)',
                description: t('sendAsVCardDesc') || "Imports directly into the recipient's Contacts app",
                onClick: handleShareVcard,
              },
              {
                id: 'pdf',
                icon: '📄',
                label: t('sendAsPdfCard') || 'Send as PDF business card',
                description: t('sendAsPdfCardDesc') || 'A polished one-page PDF ready to attach in email',
                onClick: handleSharePdfCard,
              },
            ]}
          />
          {canEdit && (<>
          <button
            onClick={handleEdit}
            className="text-white hover:text-gray-200 lg:text-gray-600 lg:hover:text-blue-600 transition-colors p-1"
            title={t('edit')}
            aria-label={t('edit')}
            data-testid="contact-edit-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={handleDelete}
            className="text-white hover:text-red-200 lg:text-gray-600 lg:hover:text-red-600 transition-colors p-1"
            title={t('delete')}
            aria-label={t('delete')}
            data-testid="contact-delete-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          </>)}
        </div>
  );

  return renderLayout(
    <>
      {/* Contact Header — soft brand-gradient hero with avatar lift */}
      <div className="bg-white rounded-2xl border border-gray-200 border-t-4 border-t-blue-500 p-6 mb-4 shadow-sm">
        <div className="flex items-center mb-6">
          <div className="mr-5 w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-700 flex items-center justify-center text-2xl font-bold tracking-wide ring-1 ring-blue-200/60">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight truncate">{contact.name}</h1>
            {contact.company && (
              <p className="text-sm sm:text-base text-gray-500 mt-0.5 truncate">{contact.company}</p>
            )}
            {contact.org_id && contact.added_by_name && (
              <span
                className="inline-flex items-center gap-1 mt-2 px-2.5 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-xs font-medium text-indigo-700"
                data-testid="contact-added-by-badge"
              >
                Added by {contact.user_id === myUserId ? 'you' : contact.added_by_name}
              </span>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              className="flex items-center justify-center py-3 px-4 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              {t('call')}
            </a>
          )}
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center justify-center py-3 px-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {t('sendEmail')}
            </a>
          )}
        </div>
      </div>

      {/* Contact Information */}
      <div className="mobile-card p-6 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('contactInformation')}</h2>
        
        <div className="space-y-4">
          {contact.email && (
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('email')}</p>
                <p className="font-medium text-gray-900">{contact.email}</p>
              </div>
            </div>
          )}

          {contact.phone && (
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('phone')}</p>
                <p className="font-medium text-gray-900">{contact.phone}</p>
              </div>
            </div>
          )}

          {contact.company && (
            <div className="flex items-center">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('place')}</p>
                <p className="font-medium text-gray-900">{contact.company}</p>
              </div>
            </div>
          )}

          {contact.address && (
            <div className="flex items-start">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4 mt-1">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600">{t('address')}</p>
                <p 
                  className="font-medium text-gray-900 whitespace-pre-line cursor-pointer hover:text-blue-600 hover:underline"
                  onClick={handleAddressClick}
                  title={t('clickToOpenMaps')}
                >
                  {contact.address}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Links */}
      <CopyContactToWorkspace contact={contact} />
      {embedded && onSelectTask && <ContactTasksSection contactId={contactId} onSelectTask={onSelectTask} />}
      {!embedded && (
      <div className="mobile-card p-6 mb-4">
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            fullWidth
            onClick={() => navigate('/tasks')}
          >
            📋 {t('tasks')}
          </Button>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => navigate('/contacts')}
          >
            👥 {t('contacts')}
          </Button>
        </div>
      </div>
      )}

      {/* Edit Contact Modal */}
      {showEditModal && (
        <EditContactModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          contact={contact}
          onSuccess={handleEditSuccess}
        />
      )}

      {!embedded && <MobileBottomNav />}
    </>,
    headerActions
  );
};

export default ContactDetailsPage;